import { N, round, ceil, TE_CABLES, CU_R, TE_CPC, CA_PVC, CG_BUNCHED, MCB_MULT, teRow } from './helpers.mjs';

export const ELECTRICIANS = [
  // ====================== ELECTRICIANS ======================
  {
    slug: 'voltage-drop',
    trade: 'electricians',
    name: 'Voltage Drop',
    blurb: 'V_drop = mV/A/m × I × L / 1000. UK lighting 3%, power 5% max.',
    inputs: [
      { key: 'current', label: 'Load current', unit: 'A', placeholder: '16' },
      { key: 'length', label: 'Cable run', unit: 'm', placeholder: '20' },
      { key: 'mvam', label: 'mV/A/m (1mm²=44, 1.5=29, 2.5=18, 4=11, 6=7.3)', unit: '', placeholder: '18', default: 18 },
      { key: 'volts', label: 'Supply', unit: 'V', placeholder: '230', default: 230 },
    ],
    compute: (v) => {
      const vd = (N(v.mvam) * N(v.current) * N(v.length)) / 1000;
      const pct = N(v.volts) > 0 ? (vd / N(v.volts)) * 100 : 0;
      return [
        { label: 'Voltage drop', value: round(vd, 2), unit: 'V' },
        { label: 'Drop %', value: round(pct, 2), unit: '%' },
      ];
    },
  },
  {
    slug: 'lighting-design',
    trade: 'electricians',
    name: 'Lighting (Lumens)',
    blurb: 'Target lux × area = lumens needed. Living 150 lx, Kitchen 300 lx.',
    inputs: [
      { key: 'area', label: 'Room area', unit: 'm²', placeholder: '15' },
      { key: 'lux', label: 'Target lux', unit: 'lx', placeholder: '200', default: 200 },
      { key: 'perBulb', label: 'Lumens per fitting', unit: 'lm', placeholder: '800', default: 800 },
    ],
    compute: (v) => {
      const lumens = N(v.area) * N(v.lux);
      const fittings = N(v.perBulb) > 0 ? ceil(lumens / N(v.perBulb)) : 0;
      return [
        { label: 'Total lumens', value: lumens, unit: 'lm' },
        { label: 'Fittings required', value: fittings, unit: 'pcs' },
      ];
    },
  },
  {
    slug: 'sockets-count',
    trade: 'electricians',
    name: 'Sockets per Room (BS 7671 guidance)',
    blurb: 'Approx: kitchen 6–10, living 8, bedroom 4–6.',
    inputs: [
      { key: 'area', label: 'Room area', unit: 'm²', placeholder: '15' },
      { key: 'per10', label: 'Sockets per 10m²', unit: '', placeholder: '4', default: 4 },
    ],
    compute: (v) => {
      const n = ceil((N(v.area) / 10) * N(v.per10));
      return [
        { label: 'Double sockets', value: n, unit: 'pcs' },
      ];
    },
  },

  {
    slug: 'cable-size',
    trade: 'electricians',
    name: 'Cable Size (Full BS 7671 Workflow)',
    blurb: 'Sizes a Twin & Earth circuit from design current, protective device, installation method, correction factors (Ca·Cg·Ci·Cf) and voltage drop — all in one. Guidance only; verify against BS 7671.',
    inputs: [
      { key: 'ib', label: 'Design current Ib', unit: 'A', placeholder: '32' },
      { key: 'in', label: 'Protective device In', type: 'select', default: '32',
        options: [6, 10, 16, 20, 25, 32, 40, 45, 50, 63].map((n) => ({ value: String(n), label: `${n} A` })) },
      { key: 'method', label: 'Installation (reference) method', type: 'select', default: 'C',
        options: [
          { value: 'C', label: 'C — clipped direct' },
          { value: 'm100', label: '100 — above ceiling, insulation ≤100mm' },
          { value: 'm102', label: '102 — stud wall, touching surface' },
          { value: 'm103', label: '103 — stud wall, surrounded by insulation' },
          { value: 'A', label: 'A — in conduit in insulated wall' },
        ] },
      { key: 'ca', label: 'Ambient temperature', type: 'select', default: '30',
        options: [30, 35, 40, 45, 50, 55].map((t) => ({ value: String(t), label: `${t}°C (Ca ${CA_PVC[t]})` })) },
      { key: 'cg', label: 'Circuits grouped (Cg)', type: 'select', default: '1',
        options: [1, 2, 3, 4, 5, 6, 7, 8].map((n) => ({ value: String(n), label: `${n} circuit${n > 1 ? 's' : ''} (Cg ${CG_BUNCHED[n]})` })) },
      { key: 'ci', label: 'Thermal insulation (Ci)', type: 'select', default: '1',
        options: [{ value: '1', label: 'None (Ci 1.0)' }, { value: '0.5', label: 'Surrounded >0.5m (Ci 0.5)' }] },
      { key: 'cf', label: 'Protective device type (Cf)', type: 'select', default: '1',
        options: [{ value: '1', label: 'MCB / BS88 fuse (Cf 1.0)' }, { value: '0.725', label: 'BS3036 rewireable (Cf 0.725)' }] },
      { key: 'length', label: 'Circuit length', unit: 'm', placeholder: '25' },
      { key: 'vdLimit', label: 'Max voltage drop', type: 'select', default: '5',
        options: [{ value: '3', label: '3% — lighting' }, { value: '5', label: '5% — power' }] },
      { key: 'volts', label: 'Supply voltage', unit: 'V', placeholder: '230', default: 230 },
    ],
    compute: (v) => {
      const In = Number(v.in) || 0;
      const ib = N(v.ib);
      const product = (CA_PVC[Number(v.ca)] || 1) * (CG_BUNCHED[Number(v.cg)] || 1) * (parseFloat(v.ci) || 1) * (parseFloat(v.cf) || 1);
      if (In <= 0) return [{ label: 'Select a device rating', value: 0, unit: '' }];
      const itReq = In / product; // required tabulated capacity
      const U0 = N(v.volts) || 230;
      const vdMaxV = (parseFloat(v.vdLimit) / 100) * U0;
      // smallest cable meeting CCC then voltage drop
      let chosen = null; let limiting = 'current capacity';
      for (const row of TE_CABLES) {
        const iz = row[v.method] ?? row.C;
        if (iz >= itReq) {
          const vd = (row.vd * ib * N(v.length)) / 1000;
          if (vd <= vdMaxV) { chosen = row; break; }
          else { limiting = 'voltage drop'; }
        }
      }
      if (!chosen) return [
        { label: 'Required capacity It', value: round(itReq, 1), unit: 'A' },
        { label: 'Result', value: '> 16mm² — use larger cable tables', unit: '' },
      ];
      const iz = chosen[v.method] ?? chosen.C;
      const vd = (chosen.vd * ib * N(v.length)) / 1000;
      return [
        { label: 'Correction product (Ca·Cg·Ci·Cf)', value: round(product, 3), unit: '' },
        { label: 'Required tabulated capacity It', value: round(itReq, 1), unit: 'A' },
        { label: 'Minimum cable size', value: chosen.csa, unit: 'mm²' },
        { label: 'Cable capacity Iz (this method)', value: iz, unit: 'A' },
        { label: 'Voltage drop', value: round(vd, 2), unit: 'V' },
        { label: 'Voltage drop', value: round((vd / U0) * 100, 2), unit: '%' },
        { label: 'Sized on', value: limiting === 'voltage drop' ? 'voltage drop' : 'current capacity', unit: '' },
      ];
    },
  },
  {
    slug: 'current-carrying-capacity',
    trade: 'electricians',
    name: 'Current-Carrying Capacity',
    blurb: 'Tabulated capacity (Iz) of a Twin & Earth cable for the chosen reference method, then derated by ambient, grouping and insulation factors.',
    inputs: [
      { key: 'csa', label: 'Cable size', type: 'select', default: '2.5',
        options: TE_CABLES.map((r) => ({ value: String(r.csa), label: `${r.csa} mm²` })) },
      { key: 'method', label: 'Reference method', type: 'select', default: 'C',
        options: [
          { value: 'C', label: 'C — clipped direct' },
          { value: 'm100', label: '100 — above insulated ceiling' },
          { value: 'm102', label: '102 — stud wall, touching' },
          { value: 'm103', label: '103 — surrounded by insulation' },
          { value: 'A', label: 'A — conduit in insulated wall' },
        ] },
      { key: 'ca', label: 'Ambient temperature', type: 'select', default: '30',
        options: [30, 35, 40, 45, 50, 55].map((t) => ({ value: String(t), label: `${t}°C` })) },
      { key: 'cg', label: 'Circuits grouped', type: 'select', default: '1',
        options: [1, 2, 3, 4, 5, 6, 7, 8].map((n) => ({ value: String(n), label: `${n}` })) },
      { key: 'ci', label: 'Thermal insulation', type: 'select', default: '1',
        options: [{ value: '1', label: 'None' }, { value: '0.5', label: 'Surrounded >0.5m' }] },
    ],
    compute: (v) => {
      const row = teRow(v.csa); if (!row) return [{ label: 'Select a cable size', value: 0, unit: '' }];
      const iz = row[v.method] ?? row.C;
      const factor = (CA_PVC[Number(v.ca)] || 1) * (CG_BUNCHED[Number(v.cg)] || 1) * (parseFloat(v.ci) || 1);
      return [
        { label: 'Tabulated capacity Iz', value: iz, unit: 'A' },
        { label: 'Applied factor', value: round(factor, 3), unit: '' },
        { label: 'Effective in-situ capacity', value: round(iz * factor, 1), unit: 'A' },
      ];
    },
  },
  {
    slug: 'max-zs',
    trade: 'electricians',
    name: 'Maximum Zs Values',
    blurb: 'Maximum permitted earth-fault loop impedance for a circuit breaker. Zs = 0.95 × 230 ÷ Ia (Cmin 0.95). For MCBs the value is the same for 0.4s and 5s.',
    inputs: [
      { key: 'type', label: 'Device type', type: 'select', default: 'B',
        options: [{ value: 'B', label: 'Type B (5×In)' }, { value: 'C', label: 'Type C (10×In)' }, { value: 'D', label: 'Type D (20×In)' }] },
      { key: 'in', label: 'Rating In', type: 'select', default: '32',
        options: [6, 10, 16, 20, 25, 32, 40, 50, 63].map((n) => ({ value: String(n), label: `${n} A` })) },
    ],
    compute: (v) => {
      const mult = MCB_MULT[v.type] || 5;
      const In = Number(v.in) || 0;
      if (In <= 0) return [{ label: 'Select a rating', value: 0, unit: '' }];
      const ia = mult * In;
      const zs = (0.95 * 230) / ia;
      return [
        { label: 'Trip current Ia', value: ia, unit: 'A' },
        { label: `Max Zs (Type ${v.type} ${In}A)`, value: round(zs, 2), unit: 'Ω' },
        { label: 'Rule-of-thumb limit (0.8×)', value: round(zs * 0.8, 2), unit: 'Ω' },
      ];
    },
  },
  {
    slug: 'zs-loop-impedance',
    trade: 'electricians',
    name: 'Zs (Ze + R1+R2)',
    blurb: 'Earth-fault loop impedance from Ze plus the circuit R1+R2. Uses Twin & Earth resistances; optional 1.20 multiplier for conductor operating temperature.',
    inputs: [
      { key: 'ze', label: 'External earth loop Ze', unit: 'Ω', placeholder: '0.35' },
      { key: 'csa', label: 'Cable size (line)', type: 'select', default: '2.5',
        options: TE_CABLES.map((r) => ({ value: String(r.csa), label: `${r.csa}mm² (cpc ${TE_CPC[r.csa]}mm²)` })) },
      { key: 'length', label: 'Circuit length', unit: 'm', placeholder: '25' },
      { key: 'temp', label: 'Conductor temperature', type: 'select', default: '1.2',
        options: [{ value: '1', label: 'Measured / cold (20°C)' }, { value: '1.2', label: 'At operating temp (×1.20)' }] },
      { key: 'type', label: 'Device type', type: 'select', default: 'B',
        options: [{ value: 'B', label: 'Type B' }, { value: 'C', label: 'Type C' }, { value: 'D', label: 'Type D' }] },
      { key: 'in', label: 'Device rating In', type: 'select', default: '32',
        options: [6, 10, 16, 20, 25, 32, 40, 50, 63].map((n) => ({ value: String(n), label: `${n} A` })) },
    ],
    compute: (v) => {
      const row = teRow(v.csa); if (!row) return [{ label: 'Select a cable size', value: 0, unit: '' }];
      const cpc = TE_CPC[row.csa];
      const r1r2mOhm = (CU_R[row.csa] + CU_R[cpc]) * (parseFloat(v.temp) || 1); // mΩ/m
      const r1r2 = (r1r2mOhm * N(v.length)) / 1000; // Ω
      const zs = N(v.ze) + r1r2;
      const maxZs = (0.95 * 230) / ((MCB_MULT[v.type] || 5) * (Number(v.in) || 1));
      const pass = zs <= maxZs * 0.8;
      return [
        { label: 'R1+R2 (this circuit)', value: round(r1r2, 3), unit: 'Ω' },
        { label: 'Measured Zs', value: round(zs, 3), unit: 'Ω' },
        { label: `Max Zs (Type ${v.type} ${v.in}A)`, value: round(maxZs, 2), unit: 'Ω' },
        { label: 'Result vs 0.8× limit', value: pass ? '✓ PASS' : '✗ FAIL', unit: '' },
      ];
    },
  },
  {
    slug: 'maximum-demand',
    trade: 'electricians',
    name: 'Maximum Demand (Diversity)',
    blurb: 'Estimates the assessed maximum demand of a single dwelling using IET On-Site Guide diversity. Guidance only — assess each installation individually.',
    inputs: [
      { key: 'lighting', label: 'Lighting load', unit: 'W', placeholder: '800', default: 0 },
      { key: 'cooker', label: 'Cooker', unit: 'kW', placeholder: '10', default: 0 },
      { key: 'showers', label: 'Showers / instant water heaters (total)', unit: 'kW', placeholder: '9', default: 0 },
      { key: 'immersion', label: 'Immersion / thermostatic heaters', unit: 'kW', placeholder: '3', default: 0 },
      { key: 'rings', label: 'Socket (ring/radial) circuits', unit: '', placeholder: '2', default: 0 },
      { key: 'other', label: 'Other fixed load', unit: 'kW', placeholder: '0', default: 0 },
    ],
    compute: (v) => {
      const U = 230;
      const lighting = (N(v.lighting) * 0.66) / U;
      const cookerI = (N(v.cooker) * 1000) / U;
      const cooker = cookerI > 10 ? 10 + 0.3 * (cookerI - 10) : cookerI;
      const showers = (N(v.showers) * 1000) / U; // no diversity
      const immersion = (N(v.immersion) * 1000) / U; // no diversity
      const n = Math.max(0, Math.round(N(v.rings)));
      const sockets = n >= 1 ? 32 + 0.4 * 32 * (n - 1) : 0; // 100% largest + 40% remainder (32A assumed)
      const other = (N(v.other) * 1000) / U;
      const total = lighting + cooker + showers + immersion + sockets + other;
      const mainFuse = total <= 60 ? 60 : total <= 80 ? 80 : 100;
      return [
        { label: 'Lighting (66%)', value: round(lighting, 1), unit: 'A' },
        { label: 'Cooker (10A + 30%)', value: round(cooker, 1), unit: 'A' },
        { label: 'Sockets (100% + 40%)', value: round(sockets, 1), unit: 'A' },
        { label: 'Water heating (no diversity)', value: round(showers + immersion, 1), unit: 'A' },
        { label: 'Assessed maximum demand', value: round(total, 1), unit: 'A' },
        { label: 'Suggested main fuse', value: mainFuse, unit: 'A' },
      ];
    },
  },
  {
    slug: 'ring-circuit-test',
    trade: 'electricians',
    name: 'Ring Final Circuit Test',
    blurb: 'From end-to-end ring readings (r1 line, rn neutral, r2 cpc) predicts the R1+R2 / R1+Rn you should measure at every socket, and checks the ring is continuous.',
    inputs: [
      { key: 'r1', label: 'r1 — line end-to-end', unit: 'Ω', placeholder: '0.40' },
      { key: 'rn', label: 'rn — neutral end-to-end', unit: 'Ω', placeholder: '0.40' },
      { key: 'r2', label: 'r2 — cpc end-to-end', unit: 'Ω', placeholder: '0.67' },
    ],
    compute: (v) => {
      const r1 = N(v.r1), rn = N(v.rn), r2 = N(v.r2);
      if (r1 <= 0) return [{ label: 'Enter the end-to-end readings', value: 0, unit: '' }];
      const r1rn = (r1 + rn) / 4;
      const r1r2 = (r1 + r2) / 4;
      const ratio = r1 > 0 ? r2 / r1 : 0;
      const lineNeutralOk = Math.abs(r1 - rn) <= 0.05 * Math.max(r1, rn);
      return [
        { label: 'R1+Rn at each socket', value: round(r1rn, 3), unit: 'Ω' },
        { label: 'R1+R2 at each socket', value: round(r1r2, 3), unit: 'Ω' },
        { label: 'r2 / r1 ratio (≈1.67 for 2.5/1.5)', value: round(ratio, 2), unit: '' },
        { label: 'r1 ≈ rn?', value: lineNeutralOk ? '✓ balanced' : '✗ check ring', unit: '' },
      ];
    },
  },
  {
    slug: 'conduit-trunking-fill',
    trade: 'electricians',
    name: 'Conduit / Trunking Fill',
    blurb: 'Checks cable fill against the BS 7671 space-factor method (conduit 40%, trunking 45%) using single-core PVC overall sizes.',
    inputs: [
      { key: 'csa', label: 'Cable size (single core)', type: 'select', default: '2.5',
        options: [
          { value: '1', label: '1.0mm² (6.6mm² overall)' }, { value: '1.5', label: '1.5mm² (7.5)' },
          { value: '2.5', label: '2.5mm² (11.3)' }, { value: '4', label: '4mm² (14.5)' },
          { value: '6', label: '6mm² (18.9)' }, { value: '10', label: '10mm² (30.2)' }, { value: '16', label: '16mm² (41.9)' },
        ] },
      { key: 'qty', label: 'Number of cables', unit: '', placeholder: '6' },
      { key: 'container', label: 'Containment', type: 'select', default: 't-50x50',
        options: [
          { value: 'c-16', label: 'Conduit 16mm' }, { value: 'c-20', label: 'Conduit 20mm' },
          { value: 'c-25', label: 'Conduit 25mm' }, { value: 'c-32', label: 'Conduit 32mm' },
          { value: 't-50x50', label: 'Trunking 50×50mm' }, { value: 't-75x50', label: 'Trunking 75×50mm' },
          { value: 't-100x50', label: 'Trunking 100×50mm' }, { value: 't-100x100', label: 'Trunking 100×100mm' },
        ] },
    ],
    compute: (v) => {
      const CABLE_AREA = { 1: 6.6, 1.5: 7.5, 2.5: 11.3, 4: 14.5, 6: 18.9, 10: 30.2, 16: 41.9 };
      const CONT = {
        'c-16': { area: 122, sf: 0.40 }, 'c-20': { area: 224, sf: 0.40 }, 'c-25': { area: 353, sf: 0.40 }, 'c-32': { area: 607, sf: 0.40 },
        't-50x50': { area: 2500, sf: 0.45 }, 't-75x50': { area: 3750, sf: 0.45 }, 't-100x50': { area: 5000, sf: 0.45 }, 't-100x100': { area: 10000, sf: 0.45 },
      };
      const ca = CABLE_AREA[Number(v.csa)] || 11.3;
      const c = CONT[v.container] || CONT['t-50x50'];
      const qty = N(v.qty);
      if (qty <= 0) return [{ label: 'Enter number of cables', value: 0, unit: '' }];
      const total = qty * ca;
      const capacity = c.area * c.sf;
      const fill = (total / capacity) * 100;
      return [
        { label: 'Total cable area', value: round(total, 0), unit: 'mm²' },
        { label: `Usable capacity (${Math.round(c.sf * 100)}%)`, value: round(capacity, 0), unit: 'mm²' },
        { label: 'Fill', value: round(fill, 0), unit: '%' },
        { label: 'Result', value: fill <= 100 ? '✓ OK' : '✗ Too full', unit: '' },
      ];
    },
  },
  {
    slug: 'running-cost',
    trade: 'electricians',
    name: 'Appliance Running Cost',
    blurb: 'Electricity running cost of any appliance from its power, usage and your unit rate.',
    inputs: [
      { key: 'power', label: 'Power', unit: 'W', placeholder: '2000' },
      { key: 'hours', label: 'Hours per day', unit: 'h', placeholder: '3' },
      { key: 'days', label: 'Days per week', unit: '', placeholder: '7', default: 7 },
      { key: 'rate', label: 'Unit rate', unit: 'p/kWh', placeholder: '28', default: 28 },
    ],
    compute: (v) => {
      const kwhDay = (N(v.power) / 1000) * N(v.hours);
      const costDay = (kwhDay * N(v.rate)) / 100;
      const week = costDay * (N(v.days) || 7);
      return [
        { label: 'Energy per day', value: round(kwhDay, 2), unit: 'kWh' },
        { label: 'Cost per day', value: round(costDay, 2), unit: '£' },
        { label: 'Cost per week', value: round(week, 2), unit: '£' },
        { label: 'Cost per year', value: round(week * 52, 2), unit: '£' },
      ];
    },
  },
  {
    slug: 'cable-grouping',
    trade: 'electricians',
    name: 'Cable Grouping Factor (Cg)',
    blurb: 'Grouping correction factor (Cg) from BS 7671 Table 4C1 for circuits run together, with a derating example.',
    inputs: [
      { key: 'arrangement', label: 'Arrangement', type: 'select', default: 'bunched',
        options: [{ value: 'bunched', label: 'Bunched / enclosed (conduit, trunking)' }, { value: 'clipped', label: 'Single layer, clipped to surface (touching)' }] },
      { key: 'number', label: 'Number of circuits', type: 'select', default: '2',
        options: [2, 3, 4, 5, 6, 7, 8, 9].map((n) => ({ value: String(n), label: `${n}` })) },
      { key: 'csa', label: 'Example cable (clipped direct)', type: 'select', default: '2.5',
        options: TE_CABLES.map((r) => ({ value: String(r.csa), label: `${r.csa}mm² (${r.C}A)` })) },
    ],
    compute: (v) => {
      const CLIPPED = { 2: 0.85, 3: 0.79, 4: 0.75, 5: 0.73, 6: 0.72, 7: 0.72, 8: 0.71, 9: 0.7 };
      const n = Number(v.number);
      const cg = v.arrangement === 'clipped' ? (CLIPPED[n] || 0.7) : (CG_BUNCHED[n] || 0.5);
      const row = teRow(v.csa);
      return [
        { label: 'Grouping factor Cg', value: cg, unit: '' },
        { label: `Derated ${row ? row.csa : ''}mm²`, value: row ? round(row.C * cg, 1) : 0, unit: 'A' },
      ];
    },
  },
  {
    slug: 'ev-solar',
    trade: 'electricians',
    name: 'EV Charger / Solar PV',
    blurb: 'Design current, protective device and minimum Twin & Earth cable size (clipped direct) for an EV charge point or solar PV array.',
    inputs: [
      { key: 'mode', label: 'Application', type: 'select', default: 'ev',
        options: [{ value: 'ev', label: 'EV charge point' }, { value: 'solar', label: 'Solar PV array' }] },
      { key: 'power', label: 'Power (charger kW / array kWp)', unit: 'kW', placeholder: '7.4' },
      { key: 'phase', label: 'Supply', type: 'select', default: 'single',
        options: [{ value: 'single', label: 'Single phase 230V' }, { value: 'three', label: 'Three phase 400V' }] },
    ],
    compute: (v) => {
      const kw = N(v.power);
      if (kw <= 0) return [{ label: 'Enter the power rating', value: 0, unit: '' }];
      const I = v.phase === 'three' ? (kw * 1000) / (Math.sqrt(3) * 400) : (kw * 1000) / 230;
      const design = v.mode === 'solar' ? I * 1.25 : I; // PV: size for 1.25×Isc
      const devices = [6, 10, 16, 20, 25, 32, 40, 50, 63];
      const mcb = devices.find((d) => d >= design) || 63;
      const cable = TE_CABLES.find((r) => r.C >= mcb);
      return [
        { label: 'Design current', value: round(I, 1), unit: 'A' },
        { label: v.mode === 'solar' ? 'Sizing current (1.25×)' : 'Continuous load', value: round(design, 1), unit: 'A' },
        { label: 'Suggested protective device', value: mcb, unit: 'A' },
        { label: 'Min cable (T&E clipped direct)', value: cable ? cable.csa : '>16', unit: 'mm²' },
      ];
    },
  },

];
